import { getApp, getApps, initializeApp } from 'firebase/app'
import { deleteToken, getMessaging, getToken, isSupported } from 'firebase/messaging'
import { FirebaseMessaging } from '@capacitor-firebase/messaging'
import { api } from '@/lib/api-client.ts'
import { firebaseConfig, firebaseVapidKey } from '@/config/firebaseConfig.ts'
import { getPlatform, isNativePlatform } from '@/lib/platform/platformDetection'
import { getProbedWebPushSupport, probeWebPushSupport } from '@/lib/push/pushPermission'
import {
  clearPushDeviceMarker,
  demoteMarkerToPendingCleanup,
  getActionablePushCleanups,
  getPushDeviceMarker,
  isPushRegisteredForUser,
  clearPendingPushRotation,
  getPendingPushRotation,
  isPushStorageAvailable,
  removePushCleanup,
  setPendingPushRotation,
  setPushDeviceMarker,
} from '@/lib/push/pushDeviceMarker'

const SW_URL = '/firebase-messaging-sw.js'
// Dedicated, NON-ROOT scope: the app's offline/update worker (sw.js) owns '/'
// (main.tsx). Registering the FCM worker at '/' would replace it and break
// offline + chunk recovery, so the push worker gets its own scope. Push
// delivery is not scope-limited, so a narrow scope is fine.
const FCM_SW_SCOPE = '/firebase-push/'

// Serialize native token-refresh uploads and coordinate them with logout
// teardown: without this, a `tokenReceived` POST in flight during logout can
// register a replacement device AFTER teardown deleted the old id, leaving a
// logged-out user's device subscribed (shared-device leak).
let pushTokenSync: Promise<void> = Promise.resolve()
let pushTeardownInProgress = false

export type PushRegisterResult =
  /** `denied` means the USER refused the OS permission — unrecoverable in-app. */
  | { status: 'registered'; token: string }
  | { status: 'denied' }
  | { status: 'unsupported' }
  /** Retryable: network, backend, or a missing FCM token despite granted permission. */
  | { status: 'error'; error: unknown }

/**
 * Whether push is registered on this device FOR THIS USER.
 *
 * The owner argument is not optional decoration. An FCM token is per app install,
 * so on a shared device a leftover registration can belong to somebody else, and
 * answering "yes, registered" for the wrong person is how a subscription gets
 * inherited without anyone opting in.
 */
export function isPushRegisteredOnThisDevice(userId: number | null): boolean {
  return isPushRegisteredForUser(userId)
}

/**
 * Push registration: requests permission, obtains an FCM token, and registers
 * it with the backend (POST /push_devices). On native (iOS/Android) the token
 * comes from \@capacitor-firebase/messaging (native Firebase SDK); on web it's
 * bound to the dedicated messaging service worker. The returned device id is
 * stored locally so it can be removed on logout.
 */
export async function registerPush(userId: number): Promise<PushRegisterResult> {
  try {
    if (isNativePlatform()) {
      return await registerPushNative(userId)
    }
    if (typeof window === 'undefined' || !('Notification' in window)) {
      return { status: 'unsupported' }
    }
    // Consult the PRIMED capability probe before awaiting anything. `isSupported()`
    // opens an IndexedDB connection, and awaiting it here would put an async gap
    // between the user's click and the permission request — WebKit ties
    // notification requests to a live user gesture, so that gap can cost us the
    // OS dialog entirely. When the probe already resolved (the soft-prompt gate
    // primes it at login), request permission with no await in front of it. Fall
    // back to awaiting only when nothing primed it.
    const probedSupport = getProbedWebPushSupport()
    if (probedSupport === false) {
      return { status: 'unsupported' }
    }
    if (probedSupport === undefined && !(await probeWebPushSupport())) {
      return { status: 'unsupported' }
    }

    const permission = await Notification.requestPermission()
    if (permission !== 'granted') {
      return { status: 'denied' }
    }

    // Register the messaging SW on its OWN scope with the (non-secret) config on
    // its query string so the worker can initialise Firebase without its own copy.
    const swRegistration = await navigator.serviceWorker.register(
      `${SW_URL}?${new URLSearchParams(firebaseConfig).toString()}`,
      { scope: FCM_SW_SCOPE },
    )

    const messaging = getMessaging(ensureApp())
    const token = await getToken(messaging, {
      vapidKey: firebaseVapidKey,
      serviceWorkerRegistration: swRegistration,
    })

    // A missing token is a REGISTRATION failure, not a permission refusal — the
    // user may well have just granted permission. Reporting it as `denied` told
    // the user "permission was denied" when it wasn't, and lets callers treat a
    // retryable fault as a permanent, unrecoverable answer.
    if (!token) {
      return { status: 'error', error: new Error('FCM returned no web token') }
    }

    // `expectedUserId` is an ASSERTION about which session must process this, not a
    // choice of owner — the server assigns the authenticated user and refuses on
    // mismatch. It is the id captured when this call began, deliberately not re-read
    // after the permission prompt and token fetch, because the whole point is to
    // detect the session having changed across those gaps.
    // Serialise ONLY the mutating tail on the shared teardown barrier — never the
    // permission request above it. WebKit ties Notification.requestPermission() to
    // a live user gesture, so putting a promise chain in front of it would cost the
    // OS dialog entirely; that constraint is why the whole web path cannot sit on
    // the barrier the way the native one does. The row is what must not outlive a
    // teardown, and the row is created here.
    const upload = pushTokenSync
      .catch(() => undefined)
      .then(async (): Promise<PushRegisterResult> => {
        // Re-checked INSIDE the barrier: teardown may have started while the
        // permission prompt and token fetch were pending.
        if (pushTeardownInProgress) {
          return { status: 'error', error: new Error('Registration aborted by push teardown') }
        }

        const response = await api.post('/push_devices', {
          token,
          platform: 'web',
          expectedUserId: userId,
        })
        if (response.data?.id) {
          setPushDeviceMarker(userId, String(response.data.id))
        }
        return { status: 'registered', token }
      })

    // Assign synchronously (before awaiting) so a teardown starting now sees this
    // upload and waits for it, then deletes whatever id it just wrote; swallow the
    // outcome on the barrier so a handled failure does not wedge the chain.
    pushTokenSync = upload.then(
      () => undefined,
      () => undefined,
    )
    return await upload
  } catch (error) {
    return { status: 'error', error }
  }
}

/**
 * Native (iOS/Android) push registration via the native Firebase SDK. No
 * service worker / VAPID: the plugin returns the FCM token directly (on iOS it
 * exchanges the APNs token for an FCM one). Registers it as an ios/android
 * device so the backend fans out through the same FcmClient path as web.
 */
async function registerPushNative(userId: number): Promise<PushRegisterResult> {
  // Run the WHOLE registration on the shared barrier — including the
  // permission/token requests — so a logout concurrent with Enable waits for it
  // and then deletes the latest id. Without this, an in-flight Enable could
  // create a device row AFTER teardown, re-subscribing the logged-out user.
  const registration = pushTokenSync
    .catch(() => undefined)
    .then(async (): Promise<PushRegisterResult> => {
      if (pushTeardownInProgress) {
        throw new Error('Native push teardown is in progress')
      }

      const permission = await FirebaseMessaging.requestPermissions()
      if (permission.receive !== 'granted') {
        return { status: 'denied' }
      }

      const { token } = await FirebaseMessaging.getToken()
      // See the web path: no token means registration failed, not that the user
      // said no. Permission was already confirmed `granted` two lines above.
      if (!token) {
        return { status: 'error', error: new Error('FCM returned no native token') }
      }

      // Report the truth if the upload was abandoned: nothing was registered, so
      // claiming `registered` would leave the caller (and the soft-prompt policy)
      // believing this device is subscribed when it is not.
      if (!(await persistNativePushToken(token, userId))) {
        return { status: 'error', error: new Error('Registration aborted by push teardown') }
      }
      return { status: 'registered', token }
    })

  // Assign synchronously (before the first await) so teardown sees this
  // operation even while permission/token retrieval is pending; swallow the
  // outcome on the barrier so a handled failure doesn't wedge the chain.
  pushTokenSync = registration.then(
    () => undefined,
    () => undefined,
  )
  return registration
}

/**
 * POST a native FCM token as this user's ios/android device (upsert by token).
 * REGISTRATION ONLY — rotation goes through `rotateNativePushToken`.
 *
 * Refuses to upload once teardown has begun. The endpoint assigns the row to
 * whoever the SESSION COOKIE says is authenticated — it takes no owner from us —
 * so an upload that lands after logout is credited to whoever holds the session
 * by then. `logoutUser` fires teardown as a floating promise and clears the store
 * synchronously, so the login screen is reachable while this is still in flight:
 * long enough for the next person to sign in and inherit the row. Checking the
 * flag at the moment of upload, not just when the work was queued, is what makes
 * that window empty.
 */
async function persistNativePushToken(token: string, userId: number): Promise<boolean> {
  if (pushTeardownInProgress) return false
  const platform = getPlatform() === 'ios-native' ? 'ios' : 'android'
  // See the web path for why the owner is asserted rather than inferred.
  const response = await api.post('/push_devices', { token, platform, expectedUserId: userId })
  if (response.data?.id) {
    setPushDeviceMarker(userId, String(response.data.id))
  }
  return true
}

/**
 * Push a ROTATED native FCM token onto the row this user already owns.
 *
 * FCM reissues tokens on its own schedule; without forwarding the fresh one the
 * backend keeps sending to the stale token, deletes it as UNREGISTERED, and the
 * device silently stops receiving push.
 *
 * This goes through PATCH, not POST, and that is the load-bearing part. POST is the
 * upsert-by-token operation and may re-point a row at the authenticated user — fine
 * for an explicit Enable, catastrophic for an unattended rotation, because a rotation
 * landing under somebody else's session would subscribe them without their asking.
 * PATCH is owner-only on the server, so rotation cannot reassign even if the local
 * marker is wrong.
 */
export function syncNativePushToken(token: string, userId: number): Promise<void> {
  if (!isNativePlatform() || pushTeardownInProgress || !isPushRegisteredForUser(userId)) {
    return Promise.resolve()
  }

  return enqueueRotation(() => ({ token, userId }))
}

/**
 * Put a rotation on the shared serialization barrier.
 *
 * EVERY rotation goes through here — live token events and reconnect retries
 * alike. Uploads are serialised for a reason: two PATCHes in flight can land in
 * either order, so an older token could win and the device would be left pointing
 * at a token FCM has already retired. A retry path that called the uploader
 * directly would sit outside that guarantee, which is exactly the bug this shape
 * prevents.
 *
 * The token is resolved INSIDE the queued callback, not before it waits: whatever
 * was true when the work was scheduled may not be true when it runs.
 *
 * The two run-time conditions are deliberately redundant — reverting either alone
 * breaks no test, because the other still holds. Keep both: they answer different
 * questions ("is a teardown running?" vs "is this still this user's
 * registration?"), and neither can verify the one thing that ultimately decides
 * ownership, which is whose session cookie the request will carry.
 */
function enqueueRotation(resolve: () => { token: string; userId: number } | null): Promise<void> {
  pushTokenSync = pushTokenSync
    .catch(() => undefined)
    .then(async () => {
      const next = resolve()
      if (next === null) return
      if (pushTeardownInProgress || !isPushRegisteredForUser(next.userId)) {
        return
      }
      await rotateNativePushToken(next.token, next.userId)
    })
  return pushTokenSync
}

/**
 * PATCH the owned row's token. Never falls back to POST: that would turn an
 * automatic rotation into the one operation that can take a row from its owner.
 */
async function rotateNativePushToken(token: string, userId: number): Promise<void> {
  if (pushTeardownInProgress) return
  const marker = getPushDeviceMarker()
  if (marker === null || marker.userId !== userId) return

  try {
    await api.patch(`/push_devices/${marker.deviceId}`, { token })
    // Clear ONLY if the pending record is still the one we just uploaded. An
    // unconditional clear would discard a NEWER token that was remembered while
    // this request was in flight, and that token has no second chance of its own.
    clearPendingPushRotation(token)
  } catch (error) {
    const status = (error as { response?: { status?: number } })?.response?.status
    if (status === 404) {
      // The row is gone, so there is nothing left to owe. Drop the marker; re-enabling
      // is an explicit user action.
      clearPushDeviceMarker()
      clearPendingPushRotation(token)
    } else if (status === 403 || status === 409) {
      // 403 and 409 both mean the row still EXISTS and we cannot use it — API Platform
      // reads the item before evaluating the operation's security expression, so 403 is
      // "exists, not yours to touch from this session", not "gone". Either way we stop
      // reporting registered while KEEPING the obligation to delete it: dropping the
      // marker outright would throw away the only local record of a live row that still
      // targets this device. If it truly is not ours the cleanup retry gets its own 403
      // and discards it then.
      demoteMarkerToPendingCleanup()
      clearPendingPushRotation(token)
    } else {
      // Network or 5xx: the token itself would otherwise be LOST. `tokenReceived` means
      // "here is a new token", not "retry the last one", so nothing guarantees another
      // event — and meanwhile the backend keeps sending to the stale token until it goes
      // UNREGISTERED and the row is deleted, silently ending push while the marker still
      // says enabled. So remember it and reconcile later.
      setPendingPushRotation(userId, marker.deviceId, token)
    }
  }
}

/**
 * Re-attempt a token upload that failed transiently.
 *
 * Called on login and whenever the app comes back online or to the foreground — NOT
 * only on the next `tokenReceived`, because that event means "here is a new token"
 * and may never fire again. Until the fresh token lands, the backend is still aiming
 * at one it is about to retire, so push dies silently while the marker says enabled.
 *
 * Anything stale is dropped rather than replayed: a remembered token only applies to
 * the user and the exact device row it was captured for.
 */
export function retryPendingPushRotation(userId: number): Promise<void> {
  return enqueueRotation(() => {
    // Read the pending record HERE, after waiting: a newer token may have been
    // remembered while this retry sat in the queue, and replaying the stale one
    // would undo it.
    const pending = getPendingPushRotation()
    if (pending === null) return null

    const marker = getPushDeviceMarker()
    const stale =
      pending.userId !== userId ||
      marker === null ||
      marker.userId !== userId ||
      marker.deviceId !== pending.deviceId
    if (stale) {
      // Belongs to another account or another row; replaying it would be the very
      // cross-user upload this work exists to prevent.
      clearPendingPushRotation(pending.token)
      return null
    }
    return { token: pending.token, userId }
  })
}

/**
 * Everything this device owes the server: queued deletes, then a queued token. Called
 * once the user is authenticated, and again on reconnect/foreground.
 */
export async function reconcilePushDeviceState(userId: number): Promise<void> {
  await retryPendingPushCleanups(userId)
  await retryPendingPushRotation(userId)
}

/**
 * Retry deletes we still owe the server after a logout could not reach it.
 *
 * Only the owner may delete a device (PushDeviceVoter), so this runs on login for
 * the signed-in user's own leftovers. A 404 means the row is already gone. A 403
 * means it is not ours to delete — for an owner-unknown entry migrated from the
 * legacy key that is not fixable from here. Neither can ever succeed for THIS
 * user, so the entry is dropped instead of retried forever; anything else
 * (offline, 5xx) is left for the next attempt.
 */
export async function retryPendingPushCleanups(userId: number): Promise<void> {
  for (const entry of getActionablePushCleanups(userId)) {
    try {
      await api.delete(`/push_devices/${entry.deviceId}`)
      removePushCleanup(entry.deviceId)
    } catch (error) {
      const status = (error as { response?: { status?: number } })?.response?.status
      if (status === 403 || status === 404) {
        removePushCleanup(entry.deviceId)
      }
    }
  }
}

export type PushUnregisterReason =
  /** Session is ending. Nothing may survive that could read as the NEXT user's consent. */
  | 'logout'
  /** The owner pressed Disable and is still signed in; a failure must be retryable. */
  | 'user-disabled'

/**
 * Remove this device's push registration — on logout, so a shared machine stops
 * delivering the previous user's notifications, and from the explicit "Disable"
 * control. Deletes the server-side device first (session still valid), then the
 * local FCM token. Never throws.
 *
 * On native it first blocks new token-refresh uploads and waits for any in-flight
 * one, so teardown deletes the LATEST registered device id (not a stale one that
 * a concurrent refresh already replaced).
 *
 * The two reasons must diverge when the server delete FAILS, and conflating them
 * was the cross-user leak:
 *
 *  - `user-disabled` — the owner is still signed in and the server row really is
 *    still theirs, so KEEPING the marker is accurate and lets the UI report the
 *    failure and retry.
 *  - `logout` — the marker is demoted to a pending cleanup instead. The delete is
 *    still owed, but a departing user's leftover must never answer "registered
 *    here" for whoever signs in next, because the backend upserts by token and
 *    would hand them the subscription on the next token rotation.
 *
 * @returns whether the SERVER device was removed.
 */
export async function unregisterPushDevice({
  userId,
  reason,
}: {
  userId: number | null
  reason: PushUnregisterReason
}): Promise<boolean> {
  const native = isNativePlatform()
  // BOTH platforms. The barrier used to be native-only, which left the web POST
  // able to win a race against this delete: the row it creates would then outlive
  // the logout, re-subscribing a signed-out user — exactly the hazard the native
  // barrier was built for. `expectedUserId` does not help there, because at that
  // moment the session still belongs to the departing user, so the assertion holds.
  pushTeardownInProgress = true
  await pushTokenSync.catch(() => undefined)

  // Tearing down discharges any owed token upload, for BOTH reasons: `user-disabled`
  // means they want push off, and `logout` demotes the marker to a delete obligation —
  // uploading a fresh token to a row we are trying to remove is pointless either way.
  //
  // MEASURED on a real device: without this, Disable left a `pendingRotation` pointing
  // at the row it had just deleted. The staleness check in retryPendingPushRotation
  // does catch it (0 requests, record discharged on the next reconcile), so this is not
  // a leak — but "explicit Disable leaves nothing owed" is worth holding directly
  // rather than through a later cleanup pass.
  clearPendingPushRotation()

  let serverDeleted = true
  try {
    // No marker AND no storage means "cannot tell what to remove", not "nothing to
    // remove". Reporting success there would show the user "disabled" while a live
    // server row keeps targeting this device, so fail instead and let them retry.
    if (!isPushStorageAvailable()) serverDeleted = false
    const marker = getPushDeviceMarker()
    // Disable acts only on the caller's OWN registration. Someone else's marker is
    // not theirs to remove, and the delete would 403 anyway.
    const ownedByCaller = marker !== null && (userId === null || marker.userId === userId)
    if (marker !== null && (reason === 'logout' || ownedByCaller)) {
      try {
        await api.delete(`/push_devices/${marker.deviceId}`)
        clearPushDeviceMarker()
      } catch (error) {
        const status = (error as { response?: { status?: number } })?.response?.status
        if (status === 403 || status === 404) {
          // The row is already gone or no longer ours. Retaining the marker would
          // make the Notifications screen claim this device is still registered.
          clearPushDeviceMarker()
        } else {
          serverDeleted = false
          if (reason === 'logout') demoteMarkerToPendingCleanup()
        }
      }
    }

    if (native) {
      await FirebaseMessaging.deleteToken().catch(() => undefined)
      return serverDeleted
    }

    if (typeof window === 'undefined' || !('Notification' in window)) {
      return serverDeleted
    }
    try {
      if (await isSupported()) {
        await deleteToken(getMessaging(ensureApp())).catch(() => undefined)
      }
    } catch {
      // no Firebase app / unsupported — nothing to revoke locally
    }
    return serverDeleted
  } finally {
    pushTeardownInProgress = false
  }
}

/** Idempotent default-app init (avoids the "app already exists" throw). */
function ensureApp() {
  return getApps().length ? getApp() : initializeApp(firebaseConfig)
}
